import { NumberValue, Schema } from '@coveo/bueno';
import { stateKey } from '../../../app/state-key.js';
import { clearExpiredProducts, promoteChildToParent, registerInstantProducts, updateInstantProductsQuery, } from '../../../features/commerce/instant-products/instant-products-actions.js';
import { instantProductsReducer } from '../../../features/commerce/instant-products/instant-products-slice.js';
import { fetchInstantProducts } from '../../../features/commerce/search/search-actions.js';
import { hasExpired } from '../../../features/instant-items/instant-items-state.js';
import { loadReducerError } from '../../../utils/errors.js';
import { randomID } from '../../../utils/utils.js';
import { nonEmptyString, validateOptions, } from '../../../utils/validate-payload.js';
import { buildController, } from '../../controller/headless-controller.js';
import { buildCoreInteractiveProduct, } from '../core/interactive-product/headless-core-interactive-product.js';
const instantProductsOptionDefinitions = {
    searchBoxId: nonEmptyString,
    cacheTimeout: new NumberValue(),
};
const instantProductsOptionsSchema = new Schema(instantProductsOptionDefinitions);
/**
 * Creates an `InstantProducts` controller instance.
 *
 * @param engine - The headless commerce engine.
 * @param props - The configurable `InstantProducts` properties.
 * @returns An `InstantProducts` controller instance.
 *
 * @group Buildable controllers
 * @category InstantProducts
 */
export function buildInstantProducts(engine, props) {
    if (!loadInstantProductsReducers(engine)) {
        throw loadReducerError;
    }
    const controller = buildController(engine);
    const { dispatch } = engine;
    const getState = () => engine[stateKey];
    const options = {
        searchBoxId: props.options.searchBoxId || randomID('instant-products-'),
        cacheTimeout: props.options.cacheTimeout || 6e4,
    };
    validateOptions(engine, instantProductsOptionsSchema, options, 'buildInstantProducts');
    const searchBoxId = options.searchBoxId;
    dispatch(registerInstantProducts({ id: searchBoxId }));
    const getStateForSearchBox = () => getState().instantProducts[searchBoxId];
    const getCached = (q) => getStateForSearchBox().cache[q];
    const getQuery = () => getStateForSearchBox().q;
    const getProducts = () => {
        const cached = getCached(getQuery());
        if (!cached) {
            return [];
        }
        if (cached.isLoading) {
            return [];
        }
        return cached.products;
    };
    return {
        ...controller,
        updateQuery(query) {
            if (!query) {
                return;
            }
            const cached = getCached(query);
            if (!cached ||
                (!cached.isLoading && (cached.error || hasExpired(cached)))) {
                dispatch(fetchInstantProducts({
                    id: searchBoxId,
                    q: query,
                    cacheTimeout: options.cacheTimeout,
                }));
            }
            dispatch(updateInstantProductsQuery({ id: searchBoxId, query }));
        },
        clearExpired() {
            dispatch(clearExpiredProducts({
                id: searchBoxId,
            }));
        },
        promoteChildToParent(child) {
            dispatch(promoteChildToParent({
                child,
                id: searchBoxId,
                query: getQuery(),
            }));
        },
        interactiveProduct(props) {
            return buildCoreInteractiveProduct(engine, {
                ...props,
                responseIdSelector: () => getStateForSearchBox().cache[getQuery()].searchUid,
            });
        },
        get state() {
            const query = getQuery();
            const cached = getCached(query);
            return {
                query,
                isLoading: cached?.isLoading || false,
                error: cached?.error || null,
                products: getProducts(),
                totalCount: cached?.totalCountFiltered || 0,
            };
        },
    };
}
function loadInstantProductsReducers(engine) {
    engine.addReducers({ instantProducts: instantProductsReducer });
    return true;
}
